import { Grip, List, PlusIcon, Trash2, TriangleAlert } from "lucide-react";
import { Reorder, useDragControls } from "motion/react";
import { memo, useCallback, useMemo } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { FormField } from "./form-field";
import { ConfigurationField } from "../../types";

import { Alert, AlertDescription } from "@/components/ui/v2/alert";
import { Button } from "@/components/ui/v2/button";
import { Label } from "@/components/ui/v2/label";

interface ListItem {
  id: string;
  values: {
    Index: number;
    [key: string]: string | number;
  };
}

function MultiInputListField({
  field,
  disabled,
  clientId,
}: {
  field: Extract<ConfigurationField, { type: "multi-input-list" }>;
  disabled: boolean;
  clientId?: string;
}) {
  const { t } = useTranslation();
  const { control, getValues, setValue } =
    useFormContext<Record<string, ListItem[]>>();

  // Use useFieldArray to manage the list - form state is the source of truth
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: field.key as never,
  });

  // Watch form values reactively
  const formValues = useWatch({ control, name: field.key });

  // Build items array from form state - this is what we render
  const items = useMemo(() => {
    const currentValues = formValues || [];
    return fields.map((fieldItem, index) => {
      const value = currentValues[index];
      return {
        id: fieldItem.id,
        values: {
          ...(value?.values || {}),
          Index: index,
        },
      } as ListItem;
    });
  }, [fields, formValues]);

  const handleAdd = useCallback(() => {
    const newItem: ListItem = {
      id: uuidv4(),
      values: {
        Index: fields.length,
      },
    };
    append(newItem);

    // Clear any stale form values at the new item's paths
    // React Hook Form may preserve values at paths like fieldKey.index.values.nestedFieldKey
    // even after deletion, so we need to explicitly clear them for the new item
    const newIndex = fields.length;
    if (field.options?.fields) {
      field.options.fields.forEach((nestedField) => {
        const nestedFieldPath =
          `${field.key}.${newIndex}.values.${nestedField.key}` as never;
        // Set to empty string to clear any stale values (works for both text and reference fields)
        setValue(nestedFieldPath, "" as never, { shouldDirty: false });
      });
    }
  }, [fields.length, append, field.key, field.options?.fields, setValue]);

  const handleReorder = useCallback(
    (newOrder: ListItem[]) => {
      // Get current form values to preserve all nested field data
      const currentValues = formValues || [];

      // Create a map of current values by ID for quick lookup
      const valuesById = new Map<string, ListItem>();
      currentValues.forEach((value, index) => {
        if (fields[index]) {
          valuesById.set(fields[index].id, value);
        }
      });

      // Build reordered array preserving all form values
      const reordered = newOrder.map((item, newIndex) => {
        const existingValue = valuesById.get(item.id);
        return {
          id: item.id,
          values: {
            ...(existingValue?.values || {}),
            Index: newIndex,
          },
        } as ListItem;
      });

      // Replace entire array in one operation
      replace(reordered);
    },
    [fields, formValues, replace]
  );

  const handleRemove = useCallback(
    (itemId: string) => {
      // Find the actual form index by matching the item ID
      const formIndex = fields.findIndex((f) => f.id === itemId);
      if (formIndex === -1) return;

      // Remove from form state
      remove(formIndex);

      // Update indices after removal
      setTimeout(() => {
        const currentValues = (getValues(field.key) as ListItem[]) || [];
        const reindexed = currentValues.map((item, index) => ({
          ...item,
          values: {
            ...item.values,
            Index: index,
          },
        }));
        replace(reindexed);
      }, 0);
    },
    [fields, remove, field.key, getValues, replace]
  );

  // Find all reference fields that should prevent duplicates
  // Only apply duplicate prevention to fields that have "prevent-duplicates" option set to true
  const referenceFields = useMemo(() => {
    return (
      field.options?.fields?.filter(
        (f) =>
          f.type === "reference" && f.options?.["prevent-duplicates"] === true
      ) || []
    );
  }, [field.options?.fields]);

  // Compute all IDs for each reference field (must be before early return)
  const allIdsByFieldKey = useMemo(() => {
    if (
      !formValues ||
      formValues.length === 0 ||
      referenceFields.length === 0
    ) {
      return new Map<string, string[]>();
    }

    const idsMap = new Map<string, string[]>();
    referenceFields.forEach((refField) => {
      const fieldKey = refField.key;
      const allIds = formValues
        .map((value) => value?.values?.[fieldKey])
        .filter((id): id is string => typeof id === "string" && id !== "");

      if (allIds.length > 0) {
        idsMap.set(fieldKey, allIds);
      }
    });

    return idsMap;
  }, [formValues, referenceFields]);

  if (!field.options?.fields || field.options.fields.length < 1) {
    return (
      <p className="text-sm text-destructive">
        Fields for {t(field.label)} are not defined or malformed
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between space-x-2">
        <Label htmlFor={field.key}>
          {t(field.label)}
          {field.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        <Button
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={disabled}
          type="button"
        >
          <PlusIcon className="size-4" />
          {t("common.add")}
        </Button>
      </div>
      {field.options.alert && items.length > 0 && (
        <Alert variant={field.options.alert.variant ?? "warning"}>
          <TriangleAlert />
          <AlertDescription>
            {t(field.options.alert.text)}
          </AlertDescription>
        </Alert>
      )}
      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-4 rounded-lg bg-transparent">
          <List className="size-12 text-muted-foreground mb-4" />
          <p className="text-sm font-medium text-foreground mb-1">
            {t("integrations.multiInputList.noItems", {
              defaultValue: "No items found",
            })}
          </p>
          <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
            {t("integrations.multiInputList.getStarted", {
              defaultValue: "Get started by creating your first item",
            })}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAdd}
            disabled={disabled}
            type="button"
          >
            <PlusIcon className="size-4 mr-2" />
            {t("integrations.multiInputList.addFirstItem", {
              defaultValue: t("common.add"),
            })}
          </Button>
        </div>
      ) : (
        <Reorder.Group
          axis="y"
          values={items}
          onReorder={handleReorder}
          className="space-y-2"
        >
          {items.map((item, index) => (
            <ReorderableItem
              key={item.id}
              item={item}
              fieldKey={field.key}
              nestedFields={field.options.fields}
              formIndex={index}
              itemId={item.id}
              onRemove={handleRemove}
              disabled={disabled}
              clientId={clientId}
              allIdsByFieldKey={allIdsByFieldKey}
              referenceFields={referenceFields}
              currentItemSegmentId={
                formValues?.[index]?.values?.Segment
                  ? String(formValues[index].values.Segment)
                  : undefined
              }
            />
          ))}
        </Reorder.Group>
      )}
    </div>
  );
}

const ReorderableItem = memo(
  function ReorderableItem({
    item,
    fieldKey,
    nestedFields,
    formIndex,
    itemId,
    onRemove,
    disabled,
    clientId,
    allIdsByFieldKey,
    referenceFields,
    currentItemSegmentId,
  }: {
    item: ListItem & { id: string };
    fieldKey: string;
    nestedFields: ConfigurationField[];
    formIndex: number;
    itemId: string;
    onRemove: (itemId: string) => void;
    disabled: boolean;
    clientId?: string;
    allIdsByFieldKey: Map<string, string[]>;
    referenceFields: ConfigurationField[];
    currentItemSegmentId?: string;
  }) {
    const controls = useDragControls();
    const { t } = useTranslation();

    // Memoize excludedIdsByField calculation to prevent unnecessary re-renders
    const excludedIdsByField = useMemo(() => {
      const excluded: Record<string, string[]> = {};
      referenceFields.forEach((refField) => {
        const fieldKey = refField.key;
        const allIds = allIdsByFieldKey.get(fieldKey);
        if (allIds) {
          const excludedList = allIds.filter(
            (id) => id !== currentItemSegmentId
          );
          if (excludedList.length > 0) {
            excluded[fieldKey] = excludedList;
          }
        }
      });
      return Object.keys(excluded).length > 0 ? excluded : undefined;
    }, [allIdsByFieldKey, referenceFields, currentItemSegmentId]);

    // Memoize onRemove callback to prevent unnecessary re-renders
    const handleRemove = useCallback(() => {
      onRemove(itemId);
    }, [onRemove, itemId]);

    const handlePointerDown = (e: React.PointerEvent) => {
      controls.start(e);
    };

    return (
      <Reorder.Item
        value={item}
        dragListener={false}
        dragControls={controls}
        className="relative flex items-start gap-3 p-4 border rounded-lg bg-card/10 backdrop-blur-2xl w-full"
      >
        <button
          type="button"
          className="mt-1 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors"
          onPointerDown={handlePointerDown}
          disabled={disabled}
          aria-label={t("common.drag") || "Drag to reorder"}
        >
          <Grip className="size-5" />
        </button>
        <div className="flex flex-col gap-2 w-full">
          {nestedFields.map((nestedField) => (
            <NestedFormField
              key={nestedField.key}
              field={nestedField}
              fieldKey={fieldKey}
              index={formIndex}
              disabled={disabled}
              clientId={clientId}
              excludedIdsByField={excludedIdsByField}
            />
          ))}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleRemove}
          disabled={disabled}
          className="mt-1"
          aria-label={t("common.delete") || "Delete"}
        >
          <Trash2 className="size-4" />
        </Button>
      </Reorder.Item>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    // Only re-render if these props actually change
    return (
      prevProps.item.id === nextProps.item.id &&
      prevProps.item.values.Index === nextProps.item.values.Index &&
      prevProps.disabled === nextProps.disabled &&
      prevProps.clientId === nextProps.clientId &&
      prevProps.formIndex === nextProps.formIndex &&
      prevProps.currentItemSegmentId === nextProps.currentItemSegmentId &&
      prevProps.allIdsByFieldKey === nextProps.allIdsByFieldKey &&
      prevProps.referenceFields === nextProps.referenceFields
    );
  }
);

const NestedFormField = memo(
  function NestedFormField({
    field,
    fieldKey,
    index,
    disabled,
    clientId,
    excludedIdsByField,
  }: {
    field: ConfigurationField;
    fieldKey: string;
    index: number;
    disabled: boolean;
    clientId?: string;
    excludedIdsByField?: Record<string, string[]>;
  }) {
    // Register nested field with proper path: fieldKey.index.values.nestedFieldKey
    const nestedFieldKey = `${fieldKey}.${index}.values.${field.key}`;

    // Get excluded IDs for this specific field if it's a reference field
    const excludeIds =
      field.type === "reference" && excludedIdsByField
        ? excludedIdsByField[field.key]
        : undefined;

    return (
      <FormField
        disabled={disabled}
        clientId={clientId}
        excludeIds={excludeIds}
        field={{
          ...field,
          key: nestedFieldKey,
        }}
      />
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if relevant props change
    return (
      prevProps.field.key === nextProps.field.key &&
      prevProps.index === nextProps.index &&
      prevProps.disabled === nextProps.disabled &&
      prevProps.clientId === nextProps.clientId &&
      prevProps.excludedIdsByField === nextProps.excludedIdsByField
    );
  }
);

export default MultiInputListField;
